/**
 * ItemPanel — side drawer showing one row's fields + its documents.
 * Fields reuse the same inline Cell editors as the table; the documents
 * section supports drag-drop upload, preview and delete.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  XMarkIcon, ChevronRightIcon, ArrowUpTrayIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import type { BoardAttachment, BoardColumn, BoardFull, BoardGroup, BoardRow } from './types';
import { Cell, STATUS_COLORS } from './cells';
import { ATTACH_META, fmtBytes, rowTitle, resolveRoles } from './boardHelpers';
import { ThumbImg } from './FileDrawer';

const attMeta = (a: BoardAttachment) => {
  switch (a.kind) {
    case 'pdf':   return `Document PDF${a.size ? ` · ${fmtBytes(a.size)}` : ''}`;
    case 'email': return 'Message e-mail';
    case 'photo': return 'Photographie';
    case 'excel': return `Classeur Excel${a.size ? ` · ${fmtBytes(a.size)}` : ''}`;
    case 'word':  return `Document Word${a.size ? ` · ${fmtBytes(a.size)}` : ''}`;
    default:      return fmtBytes(a.size) || 'Fichier';
  }
};

export const ItemPanel: React.FC<{
  boardId: string;
  board: BoardFull;
  row: BoardRow;
  group: BoardGroup | null;
  onCellChange: (columnId: string, value: unknown) => void;
  onUpload: (files: FileList) => void;
  onDeleteAttachment: (attId: string) => void;
  onOpenFile: (a: BoardAttachment, siblings: BoardAttachment[]) => void;
  onClose: () => void;
  uploading?: boolean;
}> = ({ boardId, board, row, group, onCellChange, onUpload, onDeleteAttachment, onOpenFile, onClose, uploading }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const { titleCol } = resolveRoles(board.columns);
  // Skip the title column (shown in the header) and any "files" column
  // (attachments are shown in the Documents section below).
  const fieldCols = board.columns.filter(
    (c: BoardColumn) => c.id !== titleCol?.id && !(c.kind === 'text' && c.config?.variant === 'files'),
  );
  const docs = row.attachments || [];
  const palette = group ? STATUS_COLORS[group.color] || STATUS_COLORS.gray : STATUS_COLORS.gray;

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);

  return (
    <>
      <div className="bd-scrim" onClick={onClose} />
      <div className="bd-drawer bd-ipanel" role="dialog" aria-label={rowTitle(row, titleCol)}>
        <div className="bd-ip-hd">
          <div className="bd-ip-grp"><span className="bd-ip-dot" style={{ background: palette.fg }} />{group ? group.name : 'Sans groupe'}</div>
          {titleCol ? (
            <div
              className="bd-ip-title"
              contentEditable
              suppressContentEditableWarning
              onBlur={e => {
                const v = e.currentTarget.textContent || '';
                if (v !== String(row.values[titleCol.id] ?? '')) onCellChange(titleCol.id, v);
              }}
            >
              {rowTitle(row, titleCol)}
            </div>
          ) : (
            <div className="bd-ip-title">{rowTitle(row, titleCol)}</div>
          )}
          <button className="bd-ip-x" onClick={onClose} aria-label="Fermer"><XMarkIcon className="w-5 h-5" /></button>
        </div>

        <div className="bd-ip-body">
          <div className="bd-ip-fields">
            {fieldCols.map(col => (
              <div className="bd-ip-field" key={col.id}>
                <div className="bd-ip-flabel">{col.name}</div>
                <div className="bd-ip-fval">
                  <Cell column={col} value={row.values[col.id]} onChange={v => onCellChange(col.id, v)} />
                </div>
              </div>
            ))}
          </div>

          <div className="bd-ip-sec">
            <div className="bd-ip-sec-hd">
              <div className="bd-ip-sec-title">Documents <span>{docs.length}</span></div>
            </div>

            <div className="bd-ip-docs">
              {docs.map(f => {
                const meta = ATTACH_META[f.kind] || ATTACH_META.file;
                const Icon = meta.Icon;
                return (
                  <div className="bd-ip-doc" key={f.id} onClick={() => onOpenFile(f, docs)}>
                    <div className={`bd-ip-doc-ic ${f.kind === 'photo' ? 'bd-ip-doc-ic--photo' : meta.cls}`}>
                      {f.kind === 'photo' ? <ThumbImg boardId={boardId} att={f} /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div className="bd-ip-doc-txt">
                      <div className="bd-ip-doc-nm">{f.name}</div>
                      <div className="bd-ip-doc-meta">{attMeta(f)}</div>
                    </div>
                    <button
                      className="bd-icon-btn bd-ip-doc-del"
                      title="Supprimer"
                      onClick={e => { e.stopPropagation(); if (confirm(`Supprimer « ${f.name} » ?`)) onDeleteAttachment(f.id); }}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                    <span className="bd-ip-doc-go"><ChevronRightIcon className="w-4 h-4" /></span>
                  </div>
                );
              })}
            </div>

            <input ref={inputRef} type="file" multiple hidden
              onChange={e => { if (e.target.files?.length) onUpload(e.target.files); e.target.value = ''; }} />
            <div
              className={`bd-ip-drop ${over ? 'bd-ip-drop--over' : ''}`}
              onClick={() => inputRef.current?.click()}
              onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true); } }}
              onDragLeave={() => setOver(false)}
              onDrop={e => { if (e.dataTransfer.files.length) { e.preventDefault(); setOver(false); onUpload(e.dataTransfer.files); } }}
            >
              <ArrowUpTrayIcon className="w-5 h-5" />
              {uploading ? 'Envoi en cours…' : 'Glissez un PDF, e-mail ou photo ici — ou cliquez pour parcourir'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
